import { api } from '@/services/api';
import { Preferences } from '@capacitor/preferences';
import { RequestOtpResponse, ValidateSignInReponse } from '@/definitions/auth';
import { loginOneSignal, logoutOneSignal } from '@/services/onesignal.service';
import { Pro } from '@/types/pro';
import { PaginatedResponse } from '@/helpers/pagination.helper';
import { runGlobalResets } from '@/utils/globalReset';
import { ref } from 'vue';

export const proRef = ref<Pro | null>(null);

const BASE_URL = '/auth';

export const requestOtp = async (email: string) => {
  return api.post<RequestOtpResponse>(`${BASE_URL}/request-otp`, {
    channel: 'email',
    email,
  });
};

export const validateOtp = async (email: string, otp: string, secret: string) => {
  return api.post<ValidateSignInReponse>(`${BASE_URL}/validate-otp`, {
    channel: 'email',
    email,
    otp,
    secret,
  });
};

export const resetPassword = async (
  email: string,
  newPassword: string,
  jwt: string,
  oldPassword?: string,
) => {
  return api.post<ValidateSignInReponse>(`${BASE_URL}/reset-password`, {
    email,
    jwt,
    newPassword,
    oldPassword,
  });
};

export const signIn = async (email: string, password: string) => {
  const result = await api.post<ValidateSignInReponse>(`${BASE_URL}/signin`, {
    email,
    password,
  });

  await Preferences.set({
    key: 'x-refreshed-token',
    value: result.data.refreshToken,
  });

  await updateAuthStore(result.data);
};

export const checkHasRefreshToken = async () => {
  return !!(
    await Preferences.get({
      key: 'x-refreshed-token',
    })
  ).value;
};

export const refreshToken = async () => {
  const result = await api.post<ValidateSignInReponse>(
    `${BASE_URL}/refresh-token`,
    {},
    {
      headers: {
        'x-refreshed-token': (
          await Preferences.get({
            key: 'x-refreshed-token',
          })
        ).value,
      },
    },
  );

  await Preferences.set({
    key: 'jwt',
    value: result.data.token,
  });

  return result;
};

let _proCache: Pro | null = null;

export const updateAuthStore = async (response: ValidateSignInReponse) => {
  await Preferences.set({
    key: 'jwt',
    value: response.token,
  });

  await Preferences.set({
    key: 'user',
    value: JSON.stringify(response.user),
  });

  const pro = await getCurrentPro();
  _proCache = pro;
  proRef.value = pro;

  await Preferences.set({
    key: 'pro',
    value: JSON.stringify(pro),
  });

  await loginOneSignal();
};

export const initProCache = async () => {
  const result = await Preferences.get({ key: 'pro' });
  if (result.value) {
    try {
      _proCache = JSON.parse(result.value);
      proRef.value = _proCache;
    } catch {
      _proCache = null;
      proRef.value = null;
    }
  }
};

export const getJwt = async (): Promise<string | null> => {
  const { value } = await Preferences.get({ key: 'jwt' });
  return value;
};

export const isLoggedIn = async (): Promise<boolean> => {
  return !!(await getJwt());
};

export const getPro = async (): Promise<Pro> => {
  let proResult = await Preferences.get({ key: 'pro' });

  if (!proResult.value) {
    await refreshToken();
    proResult = await Preferences.get({ key: 'pro' });
  }

  if (!proResult.value) {
    throw new Error('Pro introuvable après refresh — session invalide');
  }

  try {
    return JSON.parse(proResult.value);
  } catch {
    throw new Error('Données pro corrompues — session invalide');
  }
};

export const getProSync = (): Pro | null => {
  return _proCache;
};

export const getProId = async (): Promise<number> => {
  return (await getPro()).id;
};

export const getCurrentPro = async (): Promise<Pro> => {
  const response = await api.get<PaginatedResponse<Pro>>('/cms/users/me/pro', {
    params: {
      groups: 'heavy',
    },
  });
  const pro = response.data.items[0];
  if (!pro) throw new Error('Pro introuvable dans la réponse API');
  return pro;
};

export const logout = async () => {
  _proCache = null;
  proRef.value = null;
  logoutOneSignal();

  const PERSISTENT_KEYS = ['tutorial_seen', 'tutorial_facturation_seen'];
  const preserved = await Promise.all(
    PERSISTENT_KEYS.map(async (key) => ({ key, value: (await Preferences.get({ key })).value })),
  );

  await Preferences.clear();

  for (const { key, value } of preserved) {
    if (value) await Preferences.set({ key, value });
  }

  runGlobalResets();
};
